import { useState, useRef } from "react";
import {
  Upload,
  FileText,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileUp,
  Stethoscope,
  FlaskConical,
  FileQuestion,
  ClipboardList,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { motion, AnimatePresence } from "framer-motion";
import { useClientData } from "@/hooks/useClientData";
import { useI18n } from "@/contexts/I18nContext";

const DOC_TYPES = [
  { value: "gesundheitsbericht" as const, labelKey: "gesundheitsbericht" as const, icon: ClipboardList, color: "text-yellow-600 bg-yellow-50" },
  { value: "laborbefund" as const, labelKey: "laborbefund" as const, icon: FlaskConical, color: "text-amber-600 bg-amber-50" },
  { value: "ernaehrungsplan" as const, labelKey: "ernaehrungsplan" as const, icon: Stethoscope, color: "text-lime-600 bg-lime-50" },
  { value: "rezept" as const, labelKey: "rezept" as const, icon: FileQuestion, color: "text-blue-600 bg-blue-50" },
  { value: "sonstiges" as const, labelKey: "sonstiges" as const, icon: FileQuestion, color: "text-gray-600 bg-gray-50" },
] as const;

type DocType = typeof DOC_TYPES[number]["value"];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = [
  "application/pdf",
  "text/plain",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function AppDocuments() {
  const { t } = useI18n();
  const clientData = useClientData();
  const clientId = clientData.clientId || "";
  const [selectedType, setSelectedType] = useState<DocType>("gesundheitsbericht");
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentsQuery = trpc.documents.list.useQuery(
    { clientId },
    { enabled: !!clientId }
  );

  const uploadMutation = trpc.documents.upload.useMutation({
    onSuccess: () => {
      documentsQuery.refetch();
      setUploadError(null);
    },
    onError: (err) => {
      setUploadError(err.message || t("dokumente.uploadFehler"));
    },
  });

  const deleteMutation = trpc.documents.delete.useMutation({
    onSuccess: () => documentsQuery.refetch(),
  });

  const handleFileSelect = async (file: File) => {
    setUploadError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError(t("dokumente.typNichtUnterstuetzt"));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError(t("dokumente.zuGross"));
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      uploadMutation.mutate({
        clientId,
        docType: selectedType,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        fileBase64: base64,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const documents = documentsQuery.data || [];

  const getDocTypeConfig = (type: string) =>
    DOC_TYPES.find((d) => d.value === type) || DOC_TYPES[3];

  return (
    <div className="max-w-3xl mx-auto py-6 px-4 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
          <FileText className="w-7 h-7 text-primary" />
          {t("dokumente.title")}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t("dokumente.subtitleCoaching")}
        </p>
      </div>

      {/* Document Type Selector */}
      <div>
        <label className="text-sm font-medium text-foreground mb-2 block">
          {t("dokumente.typWaehlen")}
        </label>
        <div className="flex flex-wrap gap-2">
          {DOC_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                  selectedType === type.value
                    ? `${type.color} border-current shadow-sm`
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t(`dokumente.${type.labelKey}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
          dragOver
            ? "border-primary bg-primary/5 scale-[1.01]"
            : uploadMutation.isPending
            ? "border-amber-300 bg-amber-50"
            : "border-border hover:border-primary/40 hover:bg-primary/5"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          onChange={handleInputChange}
          className="hidden"
        />

        {uploadMutation.isPending ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
            <p className="text-sm font-medium text-amber-700">
              {t("dokumente.wirdHochgeladen")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <FileUp className="w-7 h-7 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {t("dokumente.hierziehen")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("dokumente.formate")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {uploadError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            {uploadError}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Document List */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-3">
          {t("dokumente.hochgeladeneLabel", { n: String(documents.length) })}
        </h2>

        {documents.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Upload className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">{t("dokumente.keineDoc")}</p>
            <p className="text-xs mt-1">
              {t("dokumente.keineDocHilfe")}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => {
              const typeConfig = getDocTypeConfig(doc.docType);
              const Icon = typeConfig.icon;
              const isProcessing = doc.status === "processing";
              const isError = doc.status === "error";

              return (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 p-4 bg-white border border-border rounded-lg hover:shadow-sm transition-shadow"
                >
                  <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground truncate">
                        {doc.fileName}
                      </p>
                      {isProcessing && (
                        <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin shrink-0" />
                      )}
                      {doc.status === "ready" && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      )}
                      {isError && (
                        <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t(`dokumente.${typeConfig.labelKey}`)} · {(doc.fileSize / 1024).toFixed(0)} KB ·{" "}
                      {new Date(doc.createdAt).toLocaleDateString("de-DE")}
                    </p>

                    {doc.summary && (
                      <p className="text-xs text-foreground/70 mt-2 line-clamp-3 leading-relaxed">
                        {doc.summary}
                      </p>
                    )}

                    {isProcessing && (
                      <p className="text-xs text-amber-600 mt-1">
                        {t("dokumente.wirdAnalysiert")}
                      </p>
                    )}
                    {isError && (
                      <p className="text-xs text-red-600 mt-1">
                        {t("dokumente.analyseFehler")}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => deleteMutation.mutate({ docId: doc.id })}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                    aria-label={t("dokumente.loeschenAria")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
